<template>
  <div class="providers-megamenu-wrapper" ref="wrapperRef">
    <!-- Trigger Button (Apple Style Pill) -->
    <button
      type="button"
      class="providers-trigger-btn min-w-fit"
      :class="{
        'providers-trigger-btn--open': isOpen,
      }"
      @click="toggleDropdown"
      aria-haspopup="true"
      :aria-expanded="isOpen"
      :title="`Provider: ${selectedProvider.label}`"
    >
      <img
        :src="selectedProvider.logo"
        :alt="selectedProvider.label"
        class="w-20 h-7 object-contain rounded"
      />
      <i
        class="fa-solid fa-chevron-down providers-trigger-chevron"
        :class="{ 'rotate-180': isOpen }"
      ></i>
    </button>

    <!-- Backdrop Overlay (Frosted Apple Glass) -->
    <div
      v-if="isOpen"
      class="megamenu-backdrop"
      :class="
        isMenuOpenSidebar ? 'megamenu-sidebar-open' : 'megamenu-sidebar-closed'
      "
      @click="closeDropdown"
    ></div>

    <!-- Mega-Menu Full-Width Dropdown Panel (Apple Style) -->
    <transition name="megamenu-slide">
      <div
        v-if="isOpen"
        class="megamenu-panel shadow-2xl"
        :class="
          isMenuOpenSidebar
            ? 'megamenu-sidebar-open'
            : 'megamenu-sidebar-closed'
        "
        role="menu"
        @click.stop
      >
        <div class="megamenu-container">
          <!-- Top Header Bar: Title, Subtitle & Apple Spotlight Search -->
          <div class="megamenu-topbar">
            <!-- Left: Icon & Title -->
            <div class="flex items-center gap-3">
              <div class="topbar-icon-box">
                <i class="fa-solid fa-server"></i>
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <span class="topbar-title">Platform Providers</span>
                  <span class="topbar-count-badge">
                    {{ filteredProviders.length }}
                    {{
                      filteredProviders.length === 1
                        ? "Environment"
                        : "Environments"
                    }}
                  </span>
                </div>
                <div class="topbar-subtitle hidden sm:block">
                  Switch between available game provider environments
                </div>
              </div>
            </div>

            <!-- Right: Search Input (Apple Spotlight Style) -->
            <!-- <div class="search-box-wrapper">
              <div class="relative w-full sm:w-72">
                <i
                  class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none"
                ></i>
                <input
                  type="text"
                  v-model="searchQuery"
                  ref="searchInputRef"
                  placeholder="Search providers..."
                  class="search-input"
                />
                <button
                  v-if="searchQuery"
                  @click="searchQuery = ''"
                  class="search-clear-btn"
                  title="Clear search"
                  type="button"
                >
                  <i class="fa-solid fa-xmark text-xs"></i>
                </button>
              </div>
            </div> -->
          </div>

          <!-- Main Content Area: Providers List -->
          <div class="megamenu-content-wrapper">
            <!-- No Results State -->
            <div
              v-if="searchQuery && filteredProviders.length === 0"
              class="no-results-state"
            >
              <div class="no-results-icon">
                <i class="fa-solid fa-magnifying-glass"></i>
              </div>
              <p class="no-results-title">
                No provider found matching "<span class="text-slate-800">{{
                  searchQuery
                }}</span
                >"
              </p>
              <button
                type="button"
                class="reset-search-btn"
                @click="searchQuery = ''"
              >
                Reset search
              </button>
            </div>

            <!-- Providers Grid (Apple Style 2-Column Cards) -->
            <div v-else class="providers-grid">
              <div
                v-for="provider in filteredProviders"
                :key="provider.id"
                class="provider-card"
                :class="{
                  'provider-card--active': provider.id === selectedProvider.id,
                }"
                @click="handleSelect(provider.id)"
              >
                <div class="provider-card-left">
                  <!-- Provider Logo Container -->
                  <div class="provider-logo-box">
                    <img
                      :src="provider.logo"
                      :alt="provider.label"
                      class="provider-logo"
                    />
                    <div class="overlay-providers"></div>
                  </div>

                  <!-- Provider Info -->
                  <div class="provider-info">
                    <div class="provider-title-row">
                      <span
                        class="provider-name"
                        v-html="highlightMatch(provider.label)"
                      ></span>
                      <span class="provider-code-badge">{{
                        provider.initials || provider.sublabel
                      }}</span>
                      <span
                        v-if="provider.id === selectedProvider.id"
                        class="provider-active-chip"
                      >
                        Active
                      </span>
                    </div>
                    <div
                      class="provider-sublabel"
                      v-html="
                        highlightMatch(provider.description || provider.url)
                      "
                    ></div>
                  </div>
                </div>

                <!-- Right Action Button / Status -->
                <div class="provider-card-right">
                  <div
                    v-if="provider.id === selectedProvider.id"
                    class="active-indicator"
                  >
                    <i class="fa-solid fa-circle-check"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import {
  PROVIDERS,
  getActiveProvider,
  switchProvider,
} from "@/helpers/provider";
import { useHeaderNavigation } from "@/composables/useHeaderNavigation";

const store = useStore();
const { isMenuOpen, toggleMenu, closeMenu } = useHeaderNavigation();
const wrapperRef = ref(null);
const searchInputRef = ref(null);
const searchQuery = ref("");

const isOpen = computed(() => isMenuOpen("providers"));
const isMenuOpenSidebar = computed(() => Boolean(store?.state?.isMenuOpen));

const selectedProvider = computed(() => getActiveProvider());
const selectedInitials = computed(() => {
  return (
    selectedProvider.value.initials ||
    selectedProvider.value.label
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
  );
});

const filteredProviders = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return PROVIDERS;

  return PROVIDERS.filter((p) => {
    const labelMatch = p.label && p.label.toLowerCase().includes(query);
    const descMatch =
      p.description && p.description.toLowerCase().includes(query);
    const idMatch = p.id && p.id.toLowerCase().includes(query);
    const initialsMatch =
      p.initials && p.initials.toLowerCase().includes(query);
    return labelMatch || descMatch || idMatch || initialsMatch;
  });
});

const toggleDropdown = () => {
  toggleMenu("providers");
  if (isOpen.value) {
    nextTick(() => {
      if (searchInputRef.value) searchInputRef.value.focus();
    });
  }
};

const closeDropdown = () => {
  if (isOpen.value) {
    closeMenu();
    searchQuery.value = "";
  }
};

const highlightMatch = (text) => {
  if (!text) return "";
  const query = searchQuery.value.trim();
  if (!query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  return text.replace(regex, `<mark class="search-highlight">$1</mark>`);
};

const handleSelect = (providerId) => {
  if (providerId === selectedProvider.value.id) {
    closeDropdown();
    return;
  }
  closeDropdown();
  switchProvider(providerId);
};

const handleClickOutside = (event) => {
  if (wrapperRef.value && !wrapperRef.value.contains(event.target)) {
    closeDropdown();
  }
};

const handleKeyDown = (event) => {
  if (event.key === "Escape" && isOpen.value) {
    closeDropdown();
  }
};

// Body Class management for custom CSS & operator customization
watch(
  isOpen,
  (val) => {
    if (typeof document !== "undefined") {
      if (val) {
        document.body.classList.add("providers-megamenu-open");
        document.body.classList.add("megamenu-open");
      } else {
        document.body.classList.remove("providers-megamenu-open");
        document.body.classList.remove("megamenu-open");
      }
    }
  },
  { immediate: true },
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleKeyDown);
  if (typeof document !== "undefined") {
    document.body.classList.remove("providers-megamenu-open");
    document.body.classList.remove("megamenu-open");
  }
});
</script>

<style lang="scss" scoped>
.providers-megamenu-wrapper {
  position: static;
  display: inline-flex;
  align-items: center;
  min-width: fit-content;
}

/* Apple Style Trigger Button */
.providers-trigger-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.32rem 0.65rem;
  border-radius: 9999px;
  background: #f8fafc;
  color: #1e293b;
  font-weight: 600;
  font-size: 0.85rem;
  border: 1px solid #cbd5e1;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  user-select: none;

  .providers-trigger-label {
    color: inherit;
  }

  .providers-active-tag {
    font-size: 0.65rem;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 6px;
    background: #e8e8ed;
    color: #48484a;
    letter-spacing: 0.02em;
    transition: all 0.18s ease;
  }

  .providers-trigger-chevron {
    font-size: 0.68rem;
    transition: transform 0.25s ease;
    color: #64748b;
  }

  &:hover {
    background: #eef6fa;
    color: var(--primary-color, #386f99);
    border-color: var(--primary-color, #386f99);
    box-shadow: 0 4px 12px rgba(56, 111, 153, 0.12);

    .providers-active-tag {
      background: rgba(56, 111, 153, 0.15);
      color: var(--primary-color, #386f99);
    }

    .providers-trigger-chevron {
      color: var(--primary-color, #386f99);
    }
  }

  &--open {
    background: #eef6fa;
    color: var(--primary-color, #386f99);
    border-color: var(--primary-color, #386f99);
    box-shadow: 0 0 0 3px rgba(56, 111, 153, 0.18);

    .providers-active-tag {
      background: var(--primary-color, #386f99);
      color: #ffffff;
    }

    .providers-trigger-chevron {
      color: var(--primary-color, #386f99);
    }
  }
}

/* Backdrop Overlay (Frosted Apple Glass) */
.megamenu-backdrop {
  position: fixed;
  top: 80px;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 99998;
  transition: left 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    width 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &.megamenu-sidebar-open {
    left: 250px;
    width: calc(100% - 250px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }

  &.megamenu-sidebar-closed {
    left: 64px;
    width: calc(100% - 64px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }
}

/* Mega-Menu Panel (Apple Style with Distinct Colored Top Border) */
.megamenu-panel {
  position: fixed;
  top: 80px;
  right: 0;
  background: #ffffff;
  // border-top: 3px solid var(--primary-color, #386f99);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding: 1.15rem 0 1.5rem 0;
  z-index: 99999;
  box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.12),
    0 4px 16px rgba(15, 23, 42, 0.04);
  max-height: calc(100dvh - 86px);
  overflow-y: auto;
  transition: left 0.25s cubic-bezier(0.16, 1, 0.3, 1),
    width 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f8fafc;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    &:hover {
      background: #94a3b8;
    }
  }

  &.megamenu-sidebar-open {
    left: 250px;
    width: calc(100% - 250px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }

  &.megamenu-sidebar-closed {
    left: 64px;
    width: calc(100% - 64px);
    @media (max-width: 768px) {
      left: 0;
      width: 100%;
    }
  }
}

.megamenu-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 0 1.75rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
}

/* Topbar */
.megamenu-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.85rem;
  margin-bottom: 1.1rem;
  border-bottom: 1px solid #f1f5f9;
  flex-wrap: wrap;

  .topbar-icon-box {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #f5f5f7;
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #386f99);
    font-size: 0.85rem;
  }

  .topbar-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #1d1d1f;
    letter-spacing: -0.015em;
  }

  .topbar-subtitle {
    font-size: 0.725rem;
    color: #86868b;
    font-weight: 500;
    margin-top: 1px;
  }

  .topbar-count-badge {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 9999px;
    background: #f5f5f7;
    color: #6e6e73;
    border: 1px solid rgba(0, 0, 0, 0.04);
  }
}

/* Apple Spotlight Style Search Box */
.search-box-wrapper {
  .search-input {
    width: 100%;
    padding: 0.42rem 2rem 0.42rem 2.2rem;
    background: #f5f5f7;
    border: 1px solid transparent;
    border-radius: 9999px;
    font-size: 0.8rem;
    font-weight: 500;
    color: #1d1d1f;
    outline: none;
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

    &::placeholder {
      color: #86868b;
    }

    &:focus {
      background: #ffffff;
      border-color: var(--primary-color, #386f99);
      box-shadow: 0 0 0 3px rgba(56, 111, 153, 0.14);
    }
  }

  .search-clear-btn {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #86868b;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #1d1d1f;
    }
  }
}

/* Content Area */
.megamenu-content-wrapper {
  min-height: 80px;
}

/* Providers Grid (Apple Style Cards) */
.providers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.provider-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.15rem;
  background: #f5f5f7;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  gap: 1rem;

  &:hover {
    background: #ffffff;
    border-color: #cbd5e1;
    transform: translateY(-1px);
    box-shadow: 0 8px 24px -4px rgba(15, 23, 42, 0.06);

    .provider-name {
      color: var(--primary-color, #386f99);
    }
  }

  &--active {
    background: #ffffff;
    border-color: var(--primary-color, #386f99);
    box-shadow: 0 4px 16px -2px rgba(56, 111, 153, 0.14);

    .provider-logo-box {
      border-color: var(--primary-color, #386f99);
      box-shadow: 0 0 0 2px rgba(56, 111, 153, 0.12);
    }
  }
}

.provider-card-left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.provider-logo-box {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  .overlay-providers {
    background: rgb(0, 0, 0);
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  .provider-logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
    position: relative;
    z-index: 12;
  }
}

.provider-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.provider-title-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.provider-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1d1d1f;
  white-space: nowrap;
  letter-spacing: -0.01em;
  transition: color 0.18s ease;
}

.provider-code-badge {
  font-size: 0.62rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 6px;
  background: #e8e8ed;
  color: #515154;
  letter-spacing: 0.02em;
}

.provider-active-chip {
  font-size: 0.58rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 9999px;
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
  text-transform: uppercase;
}

.provider-sublabel {
  font-size: 0.725rem;
  color: #86868b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.provider-card-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.active-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem;
  border-radius: 9999px;
  background: #ffffff;
  color: var(--primary-color, #386f99);
  font-size: 0.75rem;
  font-weight: 700;
  border: 1px solid #d1e3ef;
  box-shadow: 0 1px 4px rgba(56, 111, 153, 0.1);
}

.switch-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.85rem;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: var(--primary-color, #386f99);
    border-color: var(--primary-color, #386f99);
    color: #ffffff;
    box-shadow: 0 4px 10px rgba(56, 111, 153, 0.22);
  }
}

:deep(.search-highlight) {
  background: #fef08a;
  color: #854d0e;
  font-weight: 800;
  border-radius: 2px;
  padding: 0 2px;
}

/* No Results State */
.no-results-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;

  .no-results-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #f5f5f7;
    color: #86868b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }

  .no-results-title {
    font-size: 0.85rem;
    color: #64748b;
    margin-bottom: 0.75rem;
    font-weight: 600;
  }

  .reset-search-btn {
    padding: 0.35rem 0.85rem;
    border-radius: 6px;
    background: #eef6fa;
    color: var(--primary-color, #386f99);
    border: 1px solid #d1e3ef;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--primary-color, #386f99);
      color: #ffffff;
    }
  }
}

/* Transitions */
.megamenu-slide-enter-active {
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.megamenu-slide-leave-active {
  transition: all 0.16s cubic-bezier(0.4, 0, 1, 1);
}

.megamenu-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.megamenu-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
